import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/screens/plan/constants/planDashboardMockContent";
const I18n = I18nUtil.createScoped(Translations);
/**
 * Plan Dashboard V2.8.0 — Figma 冻结 Demo fixture（H5 `91827:51840` · PC `91919:52195`）
 *
 * 仅 US2 Demo 使用；Paid 生产 Dashboard（V2.8.1+）走 API，与本文件无关。
 * 见 `planDemoArchitecture.ts`。
 */

export type PlanDashboardSpendSegmentPc = {
  widthPx?: number;
  flex?: boolean;
  barClass: string;
};

export type PlanDashboardCategoryRow = {
  label: string;
  amount: string;
  barWidthPx: number;
  barColorClass: string;
  iconName: string;
};

export type PlanDashboardTimeTabId =
  | "1W"
  | "1M"
  | "3M"
  | "6M"
  | "YTD"
  | "1Y"
  | "All";

export type PlanDashboardLegendItem = {
  label: string;
  amount: string;
  color: string;
};

export const PLAN_DASHBOARD_DEMO_COPY = {
  demoTitle: I18n.t("Demo"),
  sampleBarTitle: I18n.t("This is just sample data"),
  sampleBarSubtitle: I18n.t("Start your check-up to see yours."),
} as const;

export const PLAN_DASHBOARD_SPEND = {
  title: I18n.t("Spend by category"),
  monthLabel: I18n.t("Month"),
  total: "$6,860",
  /** H5 `91827:51881` · PC `91919:52348` */
  stackedSegments: [
    { widthPx: 114, barClass: "bg-background_brand_overlay100" },
    { widthPx: 76, barClass: "bg-background_accent4_overlay100" },
    { widthPx: 64, barClass: "bg-background_accent5_overlay100" },
    { flex: true, barClass: "bg-background_auralv2_overlay100" },
    { widthPx: 28, barClass: "bg-content_aura_lv1_primary" },
    { widthPx: 11, barClass: "bg-background_auralv3_overlay100" },
  ] satisfies PlanDashboardSpendSegmentPc[],
  categories: [
    {
      label: I18n.t("Rent and utilities"),
      amount: "$3,100",
      barWidthPx: 132,
      barColorClass: "bg-content_brand_primary",
      iconName: "home",
    },
    {
      label: I18n.t("Shopping"),
      amount: "$1,560",
      barWidthPx: 67,
      barColorClass: "bg-content_brand_primary",
      iconName: "shopping-bag",
    },
    {
      label: I18n.t("Food & dining"),
      amount: "$1,240",
      barWidthPx: 53,
      barColorClass: "bg-content_brand_primary",
      iconName: "restaurant",
    },
    {
      label: I18n.t("Groceries"),
      amount: "$720",
      barWidthPx: 31,
      barColorClass: "bg-content_brand_primary",
      iconName: "cart",
    },
    {
      label: I18n.t("Transit"),
      amount: "$240",
      barWidthPx: 10,
      barColorClass: "bg-content_brand_primary",
      iconName: "car",
    },
  ] satisfies PlanDashboardCategoryRow[],
} as const;

export const PLAN_DASHBOARD_NET_WORTH_AREA = {
  title: I18n.t("Net worth"),
  monthLabel: I18n.t("Month"),
  value: "$342,180",
  change: "$157,000 (85%)",
  period: I18n.t("Year to date"),
} as const;

export const PLAN_DASHBOARD_NET_WORTH_DUAL = {
  title: I18n.t("Spend this month"),
  dateRange: I18n.t("May 1 – 28"),
  primarySeries: {
    label: I18n.t("This month"),
    value: "$6,860",
    delta: "+$260",
    color: "#4E9675",
  },
  secondarySeries: {
    label: I18n.t("3-month average"),
    value: "$6,600",
    color: "rgba(1, 38, 43, 0.4)",
  },
  timeTabs: ["1M", "3M", "6M", "YTD", "1Y", "All"] as PlanDashboardTimeTabId[],
  activeTimeTab: "YTD" as PlanDashboardTimeTabId,
} as const;

export const PLAN_DASHBOARD_PORTFOLIO = {
  title: I18n.t("Portfolio distribution"),
  value: "$248,400",
  subtitle: I18n.t("5 asset classes across 3 accounts"),
  /** numeric amounts for data-driven donut (Figma order) */
  amounts: [138200, 62400, 28000, 7800, 12000] as const,
  legend: [
    { label: I18n.t("Stocks"), amount: "$138,200", color: "#036263" },
    { label: I18n.t("ETFs"), amount: "$62,400", color: "#D97757" },
    { label: I18n.t("Bonds"), amount: "$28,000", color: "#C0BAA7" },
    { label: I18n.t("Crypto"), amount: "$7,800", color: "#6A9BCC" },
    { label: I18n.t("Cash"), amount: "$12,000", color: "#4E9675" },
  ] satisfies PlanDashboardLegendItem[],
} as const;
